<style scoped>
.mgm-root {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #F5F6FA;
}

/* ── Top chrome — since this fullscreen dialog renders above the real
     app-bar, it needs its own title + close affordance. ── */
.mgm-topbar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 18px;
  flex-shrink: 0;
  background: #fff;
  box-shadow: 0 1px 0 #F0F0F5;
}

.mgm-topbar-title {
  flex: 1;
  min-width: 0;
  font-size: 17px;
  font-weight: 800;
  letter-spacing: -0.2px;
  color: #181C32;
}

.mgm-icon-btn {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: none;
  background: #F5F6FA;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s ease, transform 0.1s ease;
}

.mgm-icon-btn:active {
  background: #E4E6EF;
  transform: scale(0.94);
}

/* ── Category list — every row expands/collapses in place ── */
.mgm-list-view {
  flex: 1;
  overflow-y: auto;
  padding: 14px 14px 28px;
  -webkit-overflow-scrolling: touch;
}

.mgm-cards {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* Each category is its own elevated card — header always visible, body
   slides into the same card when expanded, so it reads as "opening" that
   card rather than a flat list growing. */
.mgm-card-shell {
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04), 0 4px 14px rgba(16, 24, 40, 0.05);
  overflow: hidden;
  transition: box-shadow 0.2s ease;
}

.mgm-card-shell.open {
  box-shadow: 0 2px 4px rgba(16, 24, 40, 0.05), 0 10px 24px rgba(16, 24, 40, 0.08);
}

.mgm-card {
  display: flex;
  align-items: center;
  gap: 13px;
  padding: 13px 14px;
  cursor: pointer;
}

.mgm-card:active {
  background: #FAFAFC;
}

.mgm-card-ic {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.16);
}

.mgm-card-name {
  flex: 1;
  min-width: 0;
  font-size: 14.5px;
  font-weight: 600;
  color: #181C32;
}

.mgm-chev {
  transition: transform 0.2s ease;
  transform: rotate(-90deg);
  color: #C4C7D2 !important;
}

.mgm-chev.open {
  transform: rotate(0deg);
  color: #6366F1 !important;
}

/* Expanded body — same card, separated by a hairline, indented under the
   header's icon column so it reads as nested inside that category. */
.mgm-card-body {
  padding: 2px 14px 14px 49px;
  border-top: 1px solid #F2F3F7;
  margin: 0 14px;
}

.mgm-acc-enter-active,
.mgm-acc-leave-active {
  transition: opacity 0.15s ease;
}

.mgm-acc-enter,
.mgm-acc-leave-to {
  opacity: 0;
}

.mgm-section-label {
  font-size: 11px;
  font-weight: 700;
  color: #9AA0AC;
  text-transform: uppercase;
  letter-spacing: 0.7px;
  padding: 22px 6px 10px;
}

/* "More" links grouped into one shell card, same premium language as the
   category cards above. */
.mgm-more-shell {
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04), 0 4px 14px rgba(16, 24, 40, 0.05);
  overflow: hidden;
}

.mgm-bottom-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  cursor: pointer;
}

.mgm-bottom-row:active,
.mgm-bottom-row.open {
  background: #FAFAFC;
}

.mgm-more-shell>*+* {
  border-top: 1px solid #F2F3F7;
}

.mgm-bottom-ic {
  width: 30px;
  height: 30px;
  border-radius: 9px;
  background: #F5F6FA;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.mgm-bottom-name {
  flex: 1;
  min-width: 0;
  font-size: 13.5px;
  font-weight: 500;
  color: #3F4254;
}

.mgm-bottom-body {
  padding: 2px 14px 12px 55px;
}

/* SideBarMenu leaf rows dropped straight into the "More" card need the same
   rhythm as .mgm-bottom-row so Library/Settings/Drona AI don't look like a
   different component was used to render them. */
.mgm-more-shell>>>.list_min_heigt_common {
  min-height: 46px !important;
  padding-left: 14px !important;
  padding-right: 14px !important;
}

.mgm-more-shell>>>.list_min_heigt_common:active {
  background: #FAFAFC;
}
</style>

<template>
  <div class="mgm-root">
    <div class="mgm-topbar">
      <span class="mgm-topbar-title">Menu</span>
      <button class="mgm-icon-btn" @click="$emit('to_close_MenuDialog')">
        <v-icon size="20">mdi-close</v-icon>
      </button>
    </div>

    <div class="mgm-list-view">
      <div class="mgm-cards">
        <div v-for="(item, idx) in topSections" :key="'top-' + idx" class="mgm-card-shell"
          :class="{ open: isOpen('t' + idx) }">
          <div class="mgm-card" @click="toggle('t' + idx)">
            <div class="mgm-card-ic" :style="{ background: accent(idx) }">
              <img v-if="svgUrl(item)" :src="svgUrl(item)" width="19" height="19" />
              <v-icon v-else-if="isMdiIcon(item.icon)" color="#fff" size="19">{{ item.icon }}</v-icon>
              <img v-else-if="item.icon" :src="item.icon" width="19" height="19"
                style="filter:brightness(0) invert(1)" />
            </div>
            <span class="mgm-card-name">{{ item.name }}</span>
            <v-icon size="20" class="mgm-chev" :class="{ open: isOpen('t' + idx) }">mdi-chevron-down</v-icon>
          </div>
          <transition name="mgm-acc">
            <div v-if="isOpen('t' + idx)" class="mgm-card-body">
              <MegaMenuTree :items="item.children" :section-headers="true" :pass-props="passProps"
                @to_close_MenuDialog="$emit('to_close_MenuDialog')" />
            </div>
          </transition>
        </div>
      </div>

      <template v-if="bottomSections.length">
        <div class="mgm-section-label">More</div>
        <div class="mgm-more-shell">
          <template v-for="(item, idx) in bottomSections">
            <div v-if="item.has_children && item.children && item.children.length" :key="'bd-' + idx">
              <div class="mgm-bottom-row" :class="{ open: isOpen('b' + idx) }" @click="toggle('b' + idx)">
                <span class="mgm-bottom-ic">
                  <img v-if="svgUrl(item, '#7E8299')" :src="svgUrl(item, '#7E8299')" width="16" height="16" />
                  <v-icon v-else-if="isMdiIcon(item.icon)" size="16" color="#7E8299">{{ item.icon }}</v-icon>
                  <img v-else-if="item.icon" :src="item.icon" width="16" height="16" />
                </span>
                <span class="mgm-bottom-name">{{ item.name }}</span>
                <v-icon size="18" class="mgm-chev" :class="{ open: isOpen('b' + idx) }">mdi-chevron-down</v-icon>
              </div>
              <transition name="mgm-acc">
                <div v-if="isOpen('b' + idx)" class="mgm-bottom-body">
                  <MegaMenuTree :items="item.children" :section-headers="true" :pass-props="passProps"
                    @to_close_MenuDialog="$emit('to_close_MenuDialog')" />
                </div>
              </transition>
            </div>
            <SideBarMenu v-else :key="'bl-' + idx" v-bind="passProps" :ItemObj="item"
              type_menu="Item_List_Menu_LEFTSide" @to_close_MenuDialog="$emit('to_close_MenuDialog')" />
          </template>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import SideBarMenu from "@/components/Menu/SideBarMenu.vue";
import MegaMenuTree from "./MegaMenuTree.vue";
import { buildMegaMenuSections, isMdiIcon, svgIconUrl } from "./megaMenuData";

const CARD_ACCENTS = [
  "linear-gradient(135deg, #6366F1, #4338CA)",
  "linear-gradient(135deg, #EC4899, #BE185D)",
  "linear-gradient(135deg, #F59E0B, #D97706)",
  "linear-gradient(135deg, #10B981, #047857)",
  "linear-gradient(135deg, #3B82F6, #1D4ED8)",
  "linear-gradient(135deg, #8B5CF6, #6D28D9)",
];

export default {
  name: "MegaMenuMobile",
  components: { SideBarMenu, MegaMenuTree },
  props: {
    is_board_page: { type: Boolean, default: false },
    is__adani_page: { type: Boolean, default: false },
    is__gmr_page: { type: Boolean, default: false },
    is__vardhman_page: { type: Boolean, default: false },
    is__parle_page: { type: Boolean, default: false },
    is__granules_page: { type: Boolean, default: false },
    is__emami_page: { type: Boolean, default: false },
    is__family_page: { type: Boolean, default: false },
    menuDialog: { type: Boolean, default: false },
  },
  data() {
    return {
      topSections: [],
      bottomSections: [],
      openMap: {},
    };
  },
  computed: {
    passProps() {
      return {
        is_board_page: this.is_board_page,
        is__adani_page: this.is__adani_page,
        is__gmr_page: this.is__gmr_page,
        is__vardhman_page: this.is__vardhman_page,
        is__parle_page: this.is__parle_page,
        is__granules_page: this.is__granules_page,
        is__emami_page: this.is__emami_page,
      };
    },
  },
  watch: {
    "$route.path"() {
      this.$nextTick(this.menu_load);
    },
    is__family_page() {
      this.$nextTick(this.menu_load);
    },
    menuDialog(isOpen) {
      if (isOpen) {
        this.openMap = {};
        this.expandActiveSections();
      }
    },
  },
  mounted() {
    this.menu_load();
  },
  methods: {
    // Auto-expands whichever card the current page lives under, using the
    // same is___select flag markSelectedByPath already stamps on parents
    // whose children match the active route (see megaMenuData.js) — no new
    // active-path logic, just reading what's already there.
    expandActiveSections() {
      this.topSections.forEach((item, idx) => {
        if (item.is___select) this.$set(this.openMap, "t" + idx, true);
      });
      this.bottomSections.forEach((item, idx) => {
        if (item.is___select) this.$set(this.openMap, "b" + idx, true);
      });
    },
    isMdiIcon,
    svgUrl(item, color = "#ffffff") {
      return svgIconUrl(item, color);
    },
    accent(idx) {
      return CARD_ACCENTS[idx % CARD_ACCENTS.length];
    },
    toggle(key) {
      // Exclusive accordion — opening one card closes every other one
      // (top-level categories and "More" entries share one open slot).
      const wasOpen = this.openMap[key];
      this.openMap = {};
      if (!wasOpen) this.$set(this.openMap, key, true);
    },
    isOpen(key) {
      return !!this.openMap[key];
    },
    menu_load() {
      const { topSections, bottomSections } = buildMegaMenuSections(
        this.is_board_page,
        this.is__family_page,
        this.$route.path
      );
      this.topSections = topSections;
      this.bottomSections = bottomSections;
      this.expandActiveSections();
    },
  },
};
</script>
